/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';

// Types
import {
	TApiResponseApiTokenPopulated,
	TApiResponseGetApiTokensPopulated,
	TParsedApiToken,
} from '../types';
import {
	TApiParams,
	TParams,
} from '@global-types/app/app';

// Utils
import { formatDate } from '@utils/functions';

// Constants
import { NOT_APPLICABLE } from '@config/constants/common';
import { queryKeys } from '@config/constants/query-keys';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getApiTokens = (
	params: TApiParams
): Promise<TApiResponseGetApiTokensPopulated> => {
	return apiClient.get('/api_tokens', {
		params: {
			page: params.page,
			pageSize: params.pageSize,
			populate: ['organization', 'entity'],
			query: params.queryParams,
			sort: params.sortParams,
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useApiTokensApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			queryKeys.app.apiTokens.list.key,
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getApiTokens({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (
		apiTokens: TApiResponseApiTokenPopulated[]
	) =>
		apiTokens.reduce(
			(
				accumulator: TParsedApiToken[],
				currentApiToken: TApiResponseApiTokenPopulated
			) => {
				const modifiedItem: TParsedApiToken = {
					...currentApiToken,
					organization:
						currentApiToken.organization?.name ??
						NOT_APPLICABLE,
					producer:
						currentApiToken.entity?.name ??
						NOT_APPLICABLE,
					expiredAt: currentApiToken?.expiredAt
						? formatDate(
								currentApiToken.expiredAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					last8chars: currentApiToken?.last8chars
						? `...${currentApiToken.last8chars}`
						: NOT_APPLICABLE,
					createdAt: currentApiToken?.createdAt
						? formatDate(
								currentApiToken.createdAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					updatedAt: currentApiToken?.updatedAt
						? formatDate(
								currentApiToken.updatedAt,
								'DD/MM/YYYY'
							)
						: NOT_APPLICABLE,
					domain: currentApiToken?.domain
						? currentApiToken.domain
						: NOT_APPLICABLE,
				};
				accumulator.push(modifiedItem);

				return accumulator;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total ?? 0,
		page: data?.page ?? 0,
		pageSize: data?.pageSize ?? 0,
		totalPages: data?.totalPages ?? 0,
	};
};
